import * as B from '@blueprintjs/core';
import {bbox, bboxPolygon, booleanIntersects} from '@turf/turf';
import classnames from 'classnames';
import * as geojson from 'geojson';
import * as I from 'immutable';
import React from 'react';

import MapOverlayDialog from 'app/components/MapOverlayDialog/MapOverlayDialog';
import {ApiFeatureCollection, GeometryOverlaySetting} from 'app/modules/Remote/FeatureCollection';
import {ApiOrganization, ApiOrganizationUser} from 'app/modules/Remote/Organization';
import {alphanumericSort} from 'app/utils/featureUtils';
import {getOrgPrefix} from 'app/utils/organizationUtils';
import {overlayIsReady} from 'app/utils/overlayUtils';

import cs from './ManageLayersDialog.module.scss';
import OverlayGeometryIcon from './OverlayGeometryIcon';
import OverlaySettingsDialog from './OverlaySettingsDialog';

interface OverlayRowProps {
  name: string;
  color: GeometryOverlaySetting['color'];
  /** Colors from the overlay's propertyColorRule, if any; drives a multi-color icon. */
  colors?: string[];
  defaultUnfilled?: boolean;
  status: GeometryOverlaySetting['status'];
  geometryType: GeometryOverlaySetting['geometryType'];
  isVisible: boolean;
  disableConfiguration: boolean;
  onToggleVisibility: (name: string, currentVisible: boolean) => void;
  onOpenSettings: () => void;
}

const OverlayRow = React.memo(function OverlayRow({
  name,
  color,
  colors,
  defaultUnfilled,
  status,
  geometryType,
  isVisible,
  disableConfiguration,
  onToggleVisibility,
  onOpenSettings,
}: OverlayRowProps) {
  return (
    <div className={classnames({[cs.overlaySelected]: isVisible}, cs.overlayRow)}>
      <B.Tooltip
        content={`Click to ${isVisible ? 'hide' : 'show'} ${name}`}
        // Allows popper.js to position the popover outside of the dialog's bounds
        modifiers={{
          preventOverflow: {enabled: false},
          hide: {enabled: false},
          flip: {enabled: false},
        }}
        position="bottom-right"
        className={cs.overlayRowTooltip}
      >
        <div className={cs.overlayRowContent} onClick={() => onToggleVisibility(name, isVisible)}>
          <div className={cs.colorSwatchWrapper}>
            <OverlayGeometryIcon
              geometryType={geometryType}
              color={color}
              colors={colors}
              filled={!defaultUnfilled}
            />
          </div>
          <div className={cs.overlayName}>
            {!overlayIsReady(status) && (
              <B.Icon icon="time" intent="warning" className={cs.overlayProcessingIcon} />
            )}
            {name}
          </div>
        </div>
      </B.Tooltip>

      {!disableConfiguration && (
        <B.AnchorButton
          className={cs.overlayRowIcon}
          minimal
          text="Edit"
          onClick={onOpenSettings}
        />
      )}
    </div>
  );
});

interface BaseProps {
  organization?: I.ImmutableOf<ApiOrganization>;
  profile?: I.ImmutableOf<ApiOrganizationUser>;
  disableConfiguration?: boolean;
  onClose: () => void;
}

interface OverlayProps {
  overlaySettings: GeometryOverlaySetting[];
  setOverlaySettings: (s: GeometryOverlaySetting[]) => Promise<void> | void;
  isOverlayVisibleByName: Record<string, boolean>;
  setIsOverlayVisibleByName: (p: Record<string, boolean>) => void;
  overlayFeatureCollections?: I.ImmutableOf<ApiFeatureCollection[]>;
  overlayBoundsByName?: Record<string, geojson.Geometry | null>;
  propertyBounds?: geojson.BBox | null;
}

/**
 * Dialog for viewing and customizing vector overlays
 * Implemented with MapOverlayDialog so that most of the map is visible while
 * you're tweaking colors.
 */
const ManageLayersDialog: React.ForwardRefRenderFunction<
  HTMLDivElement,
  BaseProps & OverlayProps
> = (
  {
    organization,
    profile,
    disableConfiguration = false,
    onClose,
    overlaySettings,
    setOverlaySettings,
    setIsOverlayVisibleByName,
    isOverlayVisibleByName,
    overlayFeatureCollections,
    overlayBoundsByName,
    propertyBounds,
  },
  ref
) => {
  const [showOther, setShowOther] = React.useState(false);

  const sortedOverlaySettings = React.useMemo(
    () => overlaySettings.sort((a, b) => alphanumericSort(a.name, b.name)),
    [overlaySettings]
  );

  const {relevant, other} = React.useMemo(() => {
    if (!propertyBounds || !overlayBoundsByName) {
      return {relevant: sortedOverlaySettings, other: [] as GeometryOverlaySetting[]};
    }

    const propertyBboxPoly = bboxPolygon(propertyBounds);
    const relevant: GeometryOverlaySetting[] = [];
    const other: GeometryOverlaySetting[] = [];

    for (const setting of sortedOverlaySettings) {
      const overlayGeom = overlayBoundsByName[setting.name];
      if (!overlayGeom) {
        relevant.push(setting);
        continue;
      }
      const overlayBboxPoly = bboxPolygon(bbox(overlayGeom));
      if (booleanIntersects(propertyBboxPoly, overlayBboxPoly)) {
        relevant.push(setting);
      } else {
        other.push(setting);
      }
    }

    return {relevant, other};
  }, [sortedOverlaySettings, overlayBoundsByName, propertyBounds]);

  const handleSaveSetting = React.useCallback(
    async (next: GeometryOverlaySetting) => {
      await setOverlaySettings(
        overlaySettings.map((s) => (s.name === next.name ? next : s))
      );
    },
    [overlaySettings, setOverlaySettings]
  );

  const handleDeleteSetting = React.useCallback(
    async (name: string) => {
      // Drop the saved per-portfolio entry; the next render's merge will
      // synthesize one from system defaults.
      await setOverlaySettings(overlaySettings.filter((s) => s.name !== name));
    },
    [overlaySettings, setOverlaySettings]
  );

  const handleToggleVisibility = React.useCallback(
    (name: string, currentVisible: boolean) => {
      setIsOverlayVisibleByName({[name]: !currentVisible});
    },
    [setIsOverlayVisibleByName]
  );

  const fcsByName = React.useMemo(() => {
    const acc: Record<string, I.ImmutableOf<ApiFeatureCollection>> = {};
    overlayFeatureCollections?.forEach((fc) => {
      if (fc) acc[fc.get('name')] = fc;
    });
    return acc;
  }, [overlayFeatureCollections]);

  const [editingOverlayName, setEditingOverlayName] = React.useState<string | null>(null);

  const editingSetting = editingOverlayName
    ? (overlaySettings.find((s) => s.name === editingOverlayName) ?? null)
    : null;
  const editingFc = editingOverlayName
    ? (fcsByName[editingOverlayName]?.toJS() as ApiFeatureCollection | undefined)
    : undefined;
  const renderOverlayRow = ({
    name,
    color,
    defaultUnfilled,
    status,
    geometryType,
    propertyColorRule,
  }: GeometryOverlaySetting) => {
    const colors = propertyColorRule ? Object.values(propertyColorRule.colors) : undefined;
    return (
      <OverlayRow
        key={name}
        name={name}
        color={color}
        colors={colors}
        defaultUnfilled={defaultUnfilled}
        status={status}
        isVisible={isOverlayVisibleByName[name]}
        disableConfiguration={disableConfiguration}
        onToggleVisibility={handleToggleVisibility}
        onOpenSettings={() => setEditingOverlayName(name)}
        geometryType={geometryType}
      />
    );
  };

  const footer =
    organization && profile ? (
      <div>
        <div className={cs.divider} />
        <a
          href={`/${getOrgPrefix(organization)}/settings/layers`}
          rel="noopener noreferrer"
          target="_blank"
        >
          <div>Explore the Lens Library</div>
        </a>
      </div>
    ) : null;

  if (editingSetting && editingFc) {
    return (
      <OverlaySettingsDialog
        setting={editingSetting}
        featureCollection={editingFc}
        onSave={handleSaveSetting}
        onDelete={() => handleDeleteSetting(editingSetting.name)}
        onClose={() => setEditingOverlayName(null)}
        geometryType={editingSetting.geometryType}
      />
    );
  }

  return (
    <MapOverlayDialog
      ref={ref}
      title="Change Overlays"
      titleIcon={<B.Icon icon="layers" style={{marginTop: 2}} />}
      className={cs.dialog}
      onClose={onClose}
      canOutsideClickClose={true}
    >
      {overlaySettings.length ? (
        <div>
          {relevant.map(renderOverlayRow)}

          {relevant.length === 0 && <div>No overlays cover this property.</div>}

          {other.length > 0 && (
            <div>
              <div className={cs.divider} />
              <button
                type="button"
                className={cs.collapsibleHeader}
                onClick={() => setShowOther(!showOther)}
              >
                <B.Icon icon={showOther ? 'chevron-down' : 'chevron-right'} />
                <span>
                  {other.length} overlay{other.length > 1 ? 's' : ''} outside this property
                </span>
              </button>
              {showOther && other.map(renderOverlayRow)}
            </div>
          )}

          {footer}
        </div>
      ) : (
        <div>
          <div>No overlays are associated with this portfolio.</div>
          {footer}
        </div>
      )}
    </MapOverlayDialog>
  );
};

// forwardRef so that our parent can get access to the div for sizing
export default React.forwardRef(ManageLayersDialog);
